import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import { db, functions } from "../../FirebaseConfig";
import { SavingOverlay } from "../../components/SavingOverlay";
import { DateField } from "../../components/DateField";
import { confirmDestructive } from "../../lib/confirm";
import { formatFecha, todaySantiago } from "../../lib/format";
import type { AvionDoc, BlackoutDoc } from "../../lib/types";

interface AvionRow extends AvionDoc {
  id: string;
}

interface BlackoutRow extends BlackoutDoc {
  id: string;
}

/**
 * /admin/aviones — flight fleet read-only summary, with the per-
 * aircraft blackout windows (admin-set unavailability ranges) listed
 * underneath. Add/remove from here. The same blackout list is read by
 * /solicitar to warn requesters before they pick a date.
 */
export function AdminAviones() {
  const [rows, setRows] = useState<AvionRow[] | null>(null);
  const [editing, setEditing] = useState<AvionRow | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const q = query(collection(db, "Aviones"), orderBy("displayName"));
    const unsub = onSnapshot(q, (snap) => {
      const list: AvionRow[] = [];
      snap.forEach((d) =>
        list.push({ id: d.id, ...(d.data() as AvionDoc) })
      );
      setRows(list);
    });
    return () => unsub();
  }, []);

  if (rows === null) {
    return <p className="text-sm text-slate-500">Cargando…</p>;
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mb-2 h-[3px] w-8 rounded-full bg-brand-700" />
          <h1 className="text-2xl font-semibold tracking-tight text-brand-900">Aviones</h1>
          <p className="mt-1 text-sm text-slate-600">
            Flota actual. Las ventanas de bloqueo (mantenimiento, etc.) se
            gestionan en la tarjeta de cada avión.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900"
        >
          + Nuevo avión
        </button>
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        {rows.map((a) => (
          <div
            key={a.id}
            className="rounded-lg border border-slate-200 bg-white shadow-sm p-5"
          >
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="text-lg font-semibold text-slate-900">
                {a.displayName}
              </h2>
              <button
                type="button"
                onClick={() => setEditing(a)}
                className="text-xs text-sky-700 hover:text-sky-900"
              >
                Editar
              </button>
            </div>
            <dl className="mt-4 space-y-1.5 text-sm">
              <Row label="Matrícula" value={a.tailNumber ?? "—"} mono />
              <Row label="Base" value={a.homeBaseIcao} />
              <Row label="Velocidad" value={`${a.speedKt} kt`} mono />
              <Row label="Pasajeros máx" value={String(a.maxPax)} mono />
              <Row label="Activo" value={a.active ? "sí" : "no"} />
              <Row
                label="Destinos frecuentes"
                value={
                  a.topAirports && a.topAirports.length > 0
                    ? a.topAirports.map((c) => c.replace(/^_/, "")).join(" · ")
                    : "—"
                }
                mono
              />
            </dl>

            <Blackouts aircraftId={a.id} />

            {(a.id === "cirrus" || a.id === "pilatus") ? (
              <div className="mt-4 flex justify-center">
                <img
                  src={`/${a.id}.png`}
                  alt={a.displayName}
                  loading="lazy"
                  className="w-3/5 max-w-[280px]"
                />
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {editing ? (
        <AvionEditor initial={editing} onClose={() => setEditing(null)} />
      ) : null}
      {creating ? (
        <AvionEditor initial={null} onClose={() => setCreating(false)} />
      ) : null}
    </div>
  );
}

function AvionEditor({
  initial,
  onClose,
}: {
  initial: AvionRow | null;
  onClose: () => void;
}) {
  const isNew = initial === null;
  const [code, setCode] = useState(initial?.id ?? "");
  const [displayName, setDisplayName] = useState(initial?.displayName ?? "");
  const [tailNumber, setTailNumber] = useState(initial?.tailNumber ?? "");
  const [speedKt, setSpeedKt] = useState(
    initial ? String(initial.speedKt) : ""
  );
  const [maxPax, setMaxPax] = useState(initial ? String(initial.maxPax) : "");
  const [homeBaseIcao, setHomeBaseIcao] = useState(initial?.homeBaseIcao ?? "");
  const [active, setActive] = useState(initial?.active ?? true);
  // Pinned-top airports for this aircraft. Stored as ICAOs; the
  // textarea lets admin paste a comma- / newline-separated list.
  // "Sugerir desde uso reciente" populates from a Vuelos count.
  const [topAirportsText, setTopAirportsText] = useState(
    (initial?.topAirports ?? []).join(", ")
  );
  const [topSuggesting, setTopSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function parseTopAirports(): string[] {
    return Array.from(
      new Set(
        topAirportsText
          .split(/[,\s\n]+/)
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean)
      )
    );
  }

  async function suggestTopAirports() {
    if (!initial) return;
    setTopSuggesting(true);
    try {
      // Pull this aircraft's flights from the past 12 months and rank
      // ICAOs by combined origin+destination frequency. Past-flights
      // only — pending requests can skew the curated list.
      const since = new Date();
      since.setUTCFullYear(since.getUTCFullYear() - 1);
      const snap = await getDocs(
        query(
          collection(db, "Vuelos"),
          where("aircraftId", "==", initial.id),
          where("startAt", ">=", Timestamp.fromDate(since))
        )
      );
      const counts = new Map<string, number>();
      snap.forEach((d) => {
        const f = d.data() as {
          originIcao?: string;
          destinationIcao?: string;
          status?: string;
        };
        if (f.status === "cancelled" || f.status === "rejected") return;
        if (f.originIcao) {
          counts.set(f.originIcao, (counts.get(f.originIcao) ?? 0) + 1);
        }
        if (f.destinationIcao) {
          counts.set(
            f.destinationIcao,
            (counts.get(f.destinationIcao) ?? 0) + 1
          );
        }
      });
      const top = Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([icao]) => icao);
      setTopAirportsText(top.join(", "));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setTopSuggesting(false);
    }
  }

  async function save() {
    setError(null);
    const c = code.trim().toLowerCase();
    const sp = parseInt(speedKt, 10);
    const px = parseInt(maxPax, 10);
    const base = homeBaseIcao.trim().toUpperCase();
    if (!/^[a-z][a-z0-9_-]{1,30}$/.test(c)) {
      return setError("ID inválido (use letras minúsculas, sin espacios).");
    }
    if (!displayName.trim()) return setError("Nombre obligatorio.");
    if (!Number.isFinite(sp) || sp <= 0) {
      return setError("Velocidad debe ser un entero positivo (kt).");
    }
    if (!Number.isFinite(px) || px <= 0) {
      return setError("Pasajeros máx debe ser un entero positivo.");
    }
    if (!/^[A-Z0-9_]{3,4}$/.test(base)) {
      return setError("Base ICAO inválido (3-4 caracteres).");
    }
    setSaving(true);
    try {
      const ref = doc(db, "Aviones", c);
      const topList = parseTopAirports();
      const data = {
        displayName: displayName.trim(),
        tailNumber: tailNumber.trim() || null,
        speedKt: sp,
        maxPax: px,
        homeBaseIcao: base,
        active,
        topAirports: topList.length > 0 ? topList : null,
      };
      if (isNew) {
        await setDoc(ref, { ...data, createdAt: serverTimestamp() });
      } else {
        await updateDoc(ref, data);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!initial) return;
    if (
      !confirmDestructive(
        `¿Borrar avión ${initial.displayName} (${initial.id})?\n\n` +
          `Si algún vuelo lo referencia, esa fila quedará rota.`
      )
    ) return;
    setSaving(true);
    try {
      await deleteDoc(doc(db, "Aviones", initial.id));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <SavingOverlay open={saving} label="Guardando…" />
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-slate-900">
          {isNew ? "Nuevo avión" : `Editar ${initial!.displayName}`}
        </h2>
        <div className="mt-4 space-y-4 text-sm">
          <div>
            <label className="block font-medium text-slate-700">ID</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={!isNew}
              placeholder="cirrus / pilatus"
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50 disabled:bg-slate-100"
            />
            <p className="mt-1 text-xs text-slate-500">
              Identificador interno, en minúsculas. No se puede cambiar.
            </p>
          </div>
          <div>
            <label className="block font-medium text-slate-700">Nombre</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
          </div>
          <div>
            <label className="block font-medium text-slate-700">
              Matrícula (opcional)
            </label>
            <input
              type="text"
              value={tailNumber}
              onChange={(e) => setTailNumber(e.target.value.toUpperCase())}
              placeholder="CC-AAB"
              maxLength={16}
              className="mt-1 block w-40 rounded-md border border-slate-300 px-3 py-2 font-mono text-sm uppercase focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-medium text-slate-700">
                Velocidad (kt)
              </label>
              <input
                type="number"
                value={speedKt}
                onChange={(e) => setSpeedKt(e.target.value)}
                min={1}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </div>
            <div>
              <label className="block font-medium text-slate-700">
                Pasajeros máx
              </label>
              <input
                type="number"
                value={maxPax}
                onChange={(e) => setMaxPax(e.target.value)}
                min={1}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </div>
          </div>
          <div>
            <label className="block font-medium text-slate-700">
              Base ICAO
            </label>
            <input
              type="text"
              value={homeBaseIcao}
              onChange={(e) => setHomeBaseIcao(e.target.value.toUpperCase())}
              maxLength={4}
              className="mt-1 block w-32 rounded-md border border-slate-300 px-3 py-2 font-mono text-sm uppercase focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
          </div>
          <label className="flex items-center gap-2 text-slate-700">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="rounded border-slate-300"
            />
            Activo
          </label>
          <div>
            <label className="block font-medium text-slate-700">
              Aeropuertos comunes (ICAO)
            </label>
            <p className="mt-1 text-xs text-slate-500">
              Lista separada por coma o salto de línea. Aparecen al
              tope del dropdown de Origen / Destino en Solicitar.
            </p>
            <textarea
              value={topAirportsText}
              onChange={(e) =>
                setTopAirportsText(e.target.value.toUpperCase())
              }
              rows={2}
              placeholder="SCEL, SCLC, SCQT, SCPG, SCVI, SCTB"
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs uppercase focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
            {initial ? (
              <button
                type="button"
                disabled={topSuggesting}
                onClick={suggestTopAirports}
                className="mt-1 text-xs text-sky-700 hover:text-sky-900 disabled:opacity-50"
                title="Cuenta los aeropuertos más usados de este avión en los últimos 12 meses (origen + destino) y rellena la lista. Puedes editar antes de Guardar."
              >
                {topSuggesting
                  ? "Calculando…"
                  : "Sugerir desde uso reciente (últimos 12 meses)"}
              </button>
            ) : null}
          </div>
          {error ? (
            <p className="rounded border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
              {error}
            </p>
          ) : null}
        </div>
        <div className="mt-5 flex items-center justify-between">
          {!isNew ? (
            <button
              type="button"
              disabled={saving}
              onClick={handleDelete}
              className="text-xs font-medium text-rose-700 hover:text-rose-900 disabled:opacity-50"
            >
              Borrar avión
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={onClose}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={save}
              className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900 disabled:opacity-50"
            >
              {saving ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Blackouts({ aircraftId }: { aircraftId: string }) {
  const [rows, setRows] = useState<BlackoutRow[] | null>(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    // Pull every blackout for this aircraft whose end is today or later;
    // past blackouts get hidden so the list stays useful. Server-side
    // single-field filter on endAt; client doesn't need an extra sort.
    const cutoff = Timestamp.fromDate(new Date(`${todaySantiago()}T00:00:00`));
    const q = query(
      collection(db, "Aviones", aircraftId, "Blackouts"),
      where("endAt", ">=", cutoff),
      orderBy("endAt", "asc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: BlackoutRow[] = [];
        snap.forEach((d) =>
          list.push({ id: d.id, ...(d.data() as BlackoutDoc) })
        );
        setRows(list);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error(`[blackouts ${aircraftId}]`, err);
        setRows([]);
      }
    );
    return () => unsub();
  }, [aircraftId]);

  return (
    <div className="mt-5 border-t border-slate-100 pt-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Bloqueos
        </h3>
        <button
          type="button"
          onClick={() => setAdding((v) => !v)}
          className="text-xs text-sky-700 hover:text-sky-900"
        >
          {adding ? "Cerrar" : "+ Agregar bloqueo"}
        </button>
      </div>

      {adding ? (
        <BlackoutForm
          aircraftId={aircraftId}
          onClose={() => setAdding(false)}
        />
      ) : null}

      {rows === null ? (
        <p className="mt-3 text-xs text-slate-500">Cargando bloqueos…</p>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-xs text-slate-500">
          Sin bloqueos vigentes ni futuros.
        </p>
      ) : (
        <ul className="mt-3 space-y-2 text-sm">
          {rows.map((b) => {
            const now = Date.now();
            const isActive =
              b.startAt.toMillis() <= now && now <= b.endAt.toMillis();
            return (
            <li
              key={b.id}
              className="flex flex-wrap items-baseline justify-between gap-2 rounded-md border-2 border-orange-400 bg-orange-50 px-3 py-2"
            >
              <div>
                <div className="font-mono text-xs text-slate-700">
                  {formatFecha(tsToIso(b.startAt))} → {formatFecha(tsToIso(b.endAt))}
                  {isActive ? (
                    <span className="ml-2 rounded bg-orange-200 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-orange-900">
                      activo
                    </span>
                  ) : null}
                </div>
                {b.reason ? (
                  <div className="text-xs text-slate-600">{b.reason}</div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={async () => {
                  // Single-confirm for blackouts (less destructive than
                  // deleting a flight or aircraft — easy to recreate if
                  // mistakenly removed).
                  if (
                    !window.confirm(
                      `¿Borrar este bloqueo (${formatFecha(tsToIso(b.startAt))} → ${formatFecha(tsToIso(b.endAt))})?`
                    )
                  ) {
                    return;
                  }
                  try {
                    await deleteDoc(
                      doc(db, "Aviones", aircraftId, "Blackouts", b.id)
                    );
                  } catch (err) {
                    alert(
                      `No se pudo borrar: ${err instanceof Error ? err.message : String(err)}`
                    );
                  }
                }}
                className="text-xs text-rose-700 hover:text-rose-900"
              >
                Borrar
              </button>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function BlackoutForm({
  aircraftId,
  onClose,
}: {
  aircraftId: string;
  onClose: () => void;
}) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<
    {
      id: string;
      publicNumber: string;
      fecha: string;
      hora: string;
      conflictKind?: "flight" | "ferry";
    }[] | null
  >(null);

  async function save() {
    setError(null);
    setConflicts(null);
    if (!startDate || !endDate) {
      return setError("Selecciona fecha de inicio y fin.");
    }
    if (startDate > endDate) {
      return setError("La fecha de inicio debe ser anterior o igual a la de fin.");
    }
    setSaving(true);
    try {
      const start = santiagoMidnight(startDate);
      const end = santiagoEndOfDay(endDate);
      // Server-side ferry-aware gate: returns every pending/approved
      // flight whose [preFerry, postFerry] occupied window touches the
      // proposed blackout window. Admin must resolve them first.
      const call = httpsCallable<
        { aircraftId: string; startAt: number; endAt: number },
        {
          flights: {
            id: string;
            publicNumber: string | null;
            fecha: string;
            hora: string;
            conflictKind?: "flight" | "ferry";
          }[];
        }
      >(functions, "findFlightsInWindow");
      const res = await call({
        aircraftId,
        startAt: start.getTime(),
        endAt: end.getTime(),
      });
      const hits = (res.data.flights || []).map((f) => ({
        id: f.id,
        publicNumber: f.publicNumber || f.id,
        fecha: f.fecha || "",
        hora: f.hora || "",
        conflictKind: f.conflictKind,
      }));
      if (hits.length > 0) {
        setConflicts(hits);
        setSaving(false);
        return;
      }
      await addDoc(collection(db, "Aviones", aircraftId, "Blackouts"), {
        startAt: Timestamp.fromDate(start),
        endAt: Timestamp.fromDate(end),
        reason: reason.trim() || null,
        createdAt: serverTimestamp(),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }

  return (
    <div className="mt-3 space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <SavingOverlay open={saving} label="Guardando bloqueo…" />
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-slate-700">
            Desde
          </label>
          <DateField
            value={startDate}
            onChange={setStartDate}
            min={todaySantiago()}
            className="mt-1"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700">
            Hasta (inclusivo)
          </label>
          <DateField
            value={endDate}
            onChange={setEndDate}
            min={startDate || todaySantiago()}
            className="mt-1"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-700">
          Motivo (opcional)
        </label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={120}
          placeholder="Mantenimiento, etc."
          className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
        />
      </div>
      {conflicts && conflicts.length > 0 ? (
        <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
          <p className="font-medium">
            Hay {conflicts.length} vuelo{conflicts.length === 1 ? "" : "s"}{" "}
            activo{conflicts.length === 1 ? "" : "s"} en este rango. Resuélvelos
            (cancelar o mover) antes de crear el bloqueo:
          </p>
          <ul className="mt-1 space-y-0.5">
            {conflicts.map((c) => (
              <li key={c.id}>
                ·{" "}
                <Link
                  to={`/admin/flights/${c.id}`}
                  className="font-mono font-semibold text-amber-900 underline hover:text-amber-700"
                >
                  {c.publicNumber}
                </Link>{" "}
                — {formatFecha(c.fecha)} {c.hora}
                {c.conflictKind === "ferry" ? (
                  <span className="ml-1 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-medium uppercase">
                    traslado
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {error ? (
        <p className="rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          disabled={saving}
          onClick={onClose}
          className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={save}
          className="rounded-md bg-brand-700 px-3 py-1 text-xs font-medium text-white hover:bg-brand-900 disabled:opacity-50"
        >
          {saving ? "Guardando…" : "Crear bloqueo"}
        </button>
      </div>
    </div>
  );
}

function santiagoMidnight(yyyymmdd: string): Date {
  // Build the wall-clock instant 00:00:00 in America/Santiago for the
  // given date, returned as a real Date (UTC). We discover the Santiago
  // offset at that moment and apply it.
  const [y, m, d] = yyyymmdd.split("-").map(Number);
  const wallUtc = Date.UTC(y!, m! - 1, d!, 0, 0, 0);
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Santiago",
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = fmt.formatToParts(new Date(wallUtc));
  const map: Record<string, string> = {};
  for (const p of parts) if (p.type !== "literal") map[p.type] = p.value;
  const observed = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second)
  );
  return new Date(wallUtc - (observed - wallUtc));
}

function santiagoEndOfDay(yyyymmdd: string): Date {
  const [y, m, d] = yyyymmdd.split("-").map(Number);
  const wallUtc = Date.UTC(y!, m! - 1, d!, 23, 59, 59);
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Santiago",
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = fmt.formatToParts(new Date(wallUtc));
  const map: Record<string, string> = {};
  for (const p of parts) if (p.type !== "literal") map[p.type] = p.value;
  const observed = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second)
  );
  return new Date(wallUtc - (observed - wallUtc));
}

function tsToIso(t: Timestamp): string {
  // Display blackout endpoints as their Santiago-local date — same
  // formatFecha convention used everywhere else. We use the UTC->ISO
  // round-trip first and then trim to YYYY-MM-DD in Santiago.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Santiago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(t.toDate());
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex justify-between">
      <dt className="text-slate-500">{label}</dt>
      <dd className={"text-slate-900 " + (mono ? "font-mono" : "")}>{value}</dd>
    </div>
  );
}
